<template>
  <div class="site-shell">
    <header class="site-nav">
      <a class="brand" href="#top" aria-label="软创科技首页">
        <img src="../assets/logo/logo2.png" alt="软创科技"/>
      </a>
      <nav class="nav-links" aria-label="主导航">
        <a href="#services">核心服务</a>
        <a href="#process">交付流程</a>
        <a href="#advantages">技术优势</a>
        <a href="#contact">联系我们</a>
      </nav>
      <a class="nav-cta" href="#contact">启动项目</a>
    </header>

    <main>
      <section id="top" class="hero-section">
        <img class="hero-bg" :src="heroBg" alt=""/>
        <div class="hero-overlay"></div>
        <div class="hero-grid"></div>

        <div class="hero-inner">
          <div class="hero-copy">
            <p class="hero-kicker">
              <span></span>
              企业级技术研发 · 软件外包 · AI 落地
            </p>
            <h1>
              <span>软创科技</span>
              <b>把复杂系统做成</b>
              <em>增长引擎</em>
            </h1>
            <p class="hero-subtitle">
              从需求洞察、架构设计到开发上线，我们用小而强的工程团队，为企业打造可增长、可迭代、可长期托付的数字化系统。
            </p>
            <div class="hero-actions">
              <a class="primary-button" href="#contact">
                <i class="fas fa-bolt"></i>
                立即咨询
              </a>
              <a class="ghost-button" href="#services">
                查看服务
                <i class="fas fa-arrow-right"></i>
              </a>
            </div>
            <div class="hero-proof">
              <div v-for="item in heroProof" :key="item.label">
                <strong>{{ item.value }}</strong>
                <span>{{ item.label }}</span>
              </div>
            </div>
          </div>

          <div class="hero-dashboard" aria-label="项目交付能力看板">
            <div class="dashboard-header">
              <span>Delivery Control</span>
              <strong>LIVE</strong>
            </div>
            <div class="signal-map">
              <span v-for="line in 5" :key="line" class="signal-line"></span>
              <div class="signal-core">
                <div class="signal-inner">
                  <span>AI</span>
                  <em>solution engine</em>
                </div>
              </div>
            </div>
            <div class="dashboard-metrics">
              <div v-for="item in dashboardMetrics" :key="item.label">
                <span>{{ item.label }}</span>
                <strong>{{ item.value }}</strong>
              </div>
            </div>
            <div class="code-window">
              <div class="window-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <pre>deploy("vision")
  .strategy("fast-iterate")
  .secure()
  .ship()</pre>
            </div>
          </div>
        </div>
      </section>

      <section class="impact-strip">
        <div class="strip-inner">
          <article v-for="metric in metrics" :key="metric.label">
            <strong>{{ metric.value }}</strong>
            <span>{{ metric.label }}</span>
          </article>
        </div>
      </section>

      <section id="services" class="service-section">
        <div class="section-heading">
          <p>Build What Matters</p>
          <h2>把想法压缩成可交付的产品战斗力</h2>
          <span>
            聚焦技术自研、外包协作和定制化解决方案，用工程能力直接响应业务目标。
          </span>
        </div>

        <div class="service-grid">
          <article v-for="(feature, index) in features" :key="feature.title" class="service-card">
            <div class="service-image">
              <img :src="feature.image" :alt="feature.title"/>
              <span>{{ index < 9 ? `0${index + 1}` : index + 1 }}</span>
            </div>
            <div class="service-body">
              <div class="service-icon">
                <i :class="feature.icon"></i>
              </div>
              <h3>{{ feature.title }}</h3>
              <p>{{ feature.description }}</p>
              <div class="tag-row">
                <span v-for="tag in feature.tags" :key="tag">{{ tag }}</span>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section id="process" class="process-section">
        <div class="section-heading compact">
          <p>Execution Flow</p>
          <h2>四段式交付，把风险前置，把结果做实</h2>
        </div>
        <div class="process-track">
          <article v-for="step in processSteps" :key="step.title" class="process-card">
            <span>{{ step.no }}</span>
            <h3>{{ step.title }}</h3>
            <p>{{ step.description }}</p>
          </article>
        </div>
      </section>

      <section id="about" class="about-section">
        <div class="about-media">
          <img :src="aboutImg" alt="软创科技团队工作场景"/>
          <div class="about-caption">
            <strong>菏泽 · 面向全国</strong>
            <span>敏捷协作，长期服务</span>
          </div>
        </div>
        <div class="about-content">
          <p class="eyebrow">关于软创科技</p>
          <h2>以工程确定性，支撑企业每一次关键升级</h2>
          <p>
            软创科技成立于 2025 年，专注为企业提供软件研发、系统集成、技术咨询与外包协作。我们相信好的官网不只展示形象，更应该清晰传达能力、效率与可信赖的交付方式。
          </p>
          <p>
            团队覆盖前端、后端、移动端、数据与云服务能力，围绕业务目标拆解需求，用稳定架构和持续迭代帮助客户完成数字化转型。
          </p>
          <div class="value-grid">
            <div v-for="value in values" :key="value.title">
              <i :class="value.icon"></i>
              <span>{{ value.title }}</span>
            </div>
          </div>
        </div>
      </section>

      <section id="advantages" class="advantage-section">
        <div class="section-heading on-light">
          <p>Tech Stack</p>
          <h2>从 App 到云端，一套团队打穿完整链路</h2>
          <span>减少沟通损耗，让设计、开发、测试、部署和运维在同一个节奏里推进。</span>
        </div>
        <div class="advantage-grid">
          <article v-for="advantage in advantages" :key="advantage.title" class="advantage-card">
            <i :class="advantage.icon"></i>
            <h3>{{ advantage.title }}</h3>
            <p>{{ advantage.description }}</p>
            <ul>
              <li v-for="item in advantage.items" :key="item">
                <i class="fas fa-check"></i>
                {{ item }}
              </li>
            </ul>
          </article>
        </div>
      </section>

      <section id="contact" class="contact-section">
        <div class="contact-copy">
          <p class="eyebrow">Start A Project</p>
          <h2>给我们一个需求，换你一个可执行方案</h2>
          <p>
            说清目标、预算、周期或当前卡点即可。我们会基于业务场景给出技术路径、阶段拆解和交付建议。
          </p>
          <div class="contact-list">
            <div v-for="item in contactItems" :key="item.label">
              <i :class="item.icon"></i>
              <span>
                <strong>{{ item.label }}</strong>
                {{ item.value }}
              </span>
            </div>
          </div>
        </div>

        <form class="contact-form" @submit.prevent="submitLead">
          <label>
            姓名
            <el-input v-model="leadForm.name" placeholder="请填写您的称呼"/>
          </label>
          <label>
            邮箱
            <el-input v-model="leadForm.email" placeholder="用于接收方案回复"/>
          </label>
          <label>
            需求
            <el-input
                v-model="leadForm.message"
                type="textarea"
                :rows="5"
                resize="none"
                placeholder="项目背景、目标、时间节点或需要解决的问题"
            />
          </label>
          <button type="submit">
            <i class="fas fa-paper-plane"></i>
            发送需求
          </button>
        </form>
      </section>
    </main>

    <footer class="site-footer">
      <div>
        <img src="../assets/logo/logo2.png" alt="软创科技"/>
        <p>技术研发与外包服务，助力企业创新发展。</p>
      </div>
      <span>© 2025 软创科技. 保留所有权利</span>
    </footer>
  </div>
</template>

<script lang="ts" setup>
import {reactive} from 'vue'
import {ElInput} from 'element-plus'
import heroBg from '@/assets/jpg/bg_ai.jpg'
import aboutImg from '@/assets/jpg/about.jpg'
import features1 from '@/assets/jpg/features1.jpg'
import features2 from '@/assets/jpg/features2.jpg'
import features3 from '@/assets/jpg/features3.jpg'

const heroProof = [
  {value: '7天', label: '完成方案梳理'},
  {value: '全链路', label: '研发到运维覆盖'},
  {value: '长期', label: '持续迭代支持'}
]

const dashboardMetrics = [
  {label: '需求响应', value: '24h'},
  {label: '交付节奏', value: 'Sprint'},
  {label: '安全策略', value: 'On'}
]

const metrics = [
  {value: '1+', label: '成功项目'},
  {value: '100%', label: '客户满意度'},
  {value: '4', label: '技术专家'},
  {value: '2年', label: '行业经验'}
]

const features = [
  {
    title: '技术自主研发',
    description: '围绕 AI、大数据、云计算和业务系统开发，搭建能长期演进的核心技术能力。',
    image: features1,
    icon: 'fas fa-brain',
    tags: ['AI 应用', '数据中台', '云原生']
  },
  {
    title: '技术外包服务',
    description: '以可控成本获得稳定研发产能，覆盖需求、设计、开发、测试、部署和运维。',
    image: features2,
    icon: 'fas fa-rocket',
    tags: ['敏捷交付', '驻场协作', '质量保障']
  },
  {
    title: '定制化解决方案',
    description: '根据业务场景定制系统架构与产品功能，让技术投入直接服务增长和管理效率。',
    image: features3,
    icon: 'fas fa-layer-group',
    tags: ['软件开发', '系统集成', '技术咨询']
  }
]

const processSteps = [
  {no: '01', title: '需求分析', description: '拆解业务目标、用户路径和系统边界，先把方向定准。'},
  {no: '02', title: '方案设计', description: '输出技术架构、阶段计划和风险清单，交付更可控。'},
  {no: '03', title: '开发实施', description: '按迭代节奏推进研发、联调、测试和验收，持续可见。'},
  {no: '04', title: '交付运维', description: '上线后持续监控、优化和支持，让系统稳定运行。'}
]

const values = [
  {title: '技术创新', icon: 'fas fa-lightbulb'},
  {title: '专业服务', icon: 'fas fa-user-shield'},
  {title: '客户至上', icon: 'fas fa-handshake'},
  {title: '持续发展', icon: 'fas fa-chart-line'}
]

const advantages = [
  {
    title: 'App 开发',
    description: 'iOS、Android 原生应用与跨平台应用研发。',
    icon: 'fas fa-mobile-alt',
    items: ['原生应用开发', '应用商店发布']
  },
  {
    title: '小程序开发',
    description: '覆盖微信、支付宝、抖音等多平台小程序。',
    icon: 'fab fa-weixin',
    items: ['微信小程序', '支付宝小程序']
  },
  {
    title: '后端开发',
    description: '高性能、可扩展的后端系统开发与架构设计。',
    icon: 'fas fa-server',
    items: ['数据库设计', 'API 开发']
  },
  {
    title: '前端开发',
    description: '现代化前端工程体系，打造高质量用户体验。',
    icon: 'fas fa-code',
    items: ['响应式设计', '前端工程化', '性能优化']
  },
  {
    title: 'Web 应用开发',
    description: '面向企业业务场景的官网、系统和平台建设。',
    icon: 'fas fa-desktop',
    items: ['企业官网', '管理系统', '电商平台']
  },
  {
    title: '技术保障',
    description: '围绕安全、性能和稳定性的持续技术支持。',
    icon: 'fas fa-shield-alt',
    items: ['安全防护', '性能监控', '7x24 支持']
  }
]

const contactItems = [
  {label: '公司地址', value: '山东省菏泽市', icon: 'fas fa-map-marker-alt'},
  {label: '电子邮箱', value: '2036573698@qq.com', icon: 'fas fa-envelope'},
  {label: '工作时间', value: '周一至周五 9:00 - 18:00', icon: 'fas fa-clock'}
]

const leadForm = reactive({
  name: '',
  email: '',
  message: ''
})

const submitLead = () => {
  const subject = encodeURIComponent(`官网咨询 - ${leadForm.name || '访客'}`)
  const body = encodeURIComponent(`姓名：${leadForm.name}\n邮箱：${leadForm.email}\n需求：${leadForm.message}`)
  window.location.href = `mailto:2036573698@qq.com?subject=${subject}&body=${body}`
}
</script>

<style scoped>
:global(html) {
  scroll-behavior: smooth;
}

:global(body) {
  background: #f5f9ff;
  color: #111827;
  font-family: Inter, "PingFang SC", "Microsoft YaHei", Arial, sans-serif;
}

.site-shell {
  min-height: 100vh;
  overflow-x: hidden;
  background: radial-gradient(circle at 82% 8%, rgba(77, 216, 255, 0.28), transparent 30%),
  radial-gradient(circle at 8% 16%, rgba(255, 207, 90, 0.2), transparent 28%),
  linear-gradient(180deg, #f6fbff 0%, #ffffff 40%, #f6f9ff 100%);
}

.site-nav {
  position: fixed;
  top: 16px;
  left: 50%;
  z-index: 20;
  width: min(1180px, calc(100% - 32px));
  height: 68px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 0 14px 0 18px;
  border: 1px solid rgba(202, 220, 244, 0.82);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.88);
  box-shadow: 0 22px 70px rgba(16, 87, 154, 0.12);
  backdrop-filter: blur(18px);
  transform: translateX(-50%);
}

.brand {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 176px;
  height: 46px;
  border-radius: 6px;
  background: #ffffff;
  box-shadow: 0 10px 28px rgba(16, 87, 154, 0.08);
}

.brand img {
  width: 148px;
  height: auto;
  display: block;
}

.nav-links {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 30px;
}

.nav-links a,
.nav-cta {
  color: #344054;
  font-size: 14px;
  line-height: 1;
  text-decoration: none;
  transition: color 0.2s ease, border-color 0.2s ease, background 0.2s ease;
}

.nav-links a:hover {
  color: #0076ff;
}

.nav-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 42px;
  padding: 0 18px;
  border: 1px solid rgba(0, 118, 255, 0.28);
  border-radius: 6px;
  color: #0057c2;
  background: rgba(237, 246, 255, 0.92);
}

.nav-cta:hover {
  background: #e4f2ff;
}

.hero-section {
  position: relative;
  min-height: 760px;
  display: flex;
  align-items: center;
  overflow: hidden;
  padding: 128px 24px 76px;
  color: #071015;
  isolation: isolate;
}

.hero-bg,
.hero-overlay,
.hero-grid {
  position: absolute;
  inset: 0;
}

.hero-bg {
  z-index: -4;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  opacity: 0.22;
  filter: saturate(1.45) contrast(1.08);
  transform: scale(1.04);
}

.hero-overlay {
  z-index: -3;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.96) 0%, rgba(247, 251, 255, 0.9) 46%, rgba(235, 247, 255, 0.72) 100%),
  linear-gradient(135deg, rgba(77, 216, 255, 0.22) 0%, transparent 32%, rgba(255, 207, 90, 0.2) 100%);
}

.hero-grid {
  z-index: -2;
  opacity: 0.8;
  background-image: linear-gradient(rgba(0, 118, 255, 0.08) 1px, transparent 1px),
  linear-gradient(90deg, rgba(0, 118, 255, 0.08) 1px, transparent 1px);
  background-size: 56px 56px;
  mask-image: linear-gradient(180deg, transparent 0%, #000 18%, #000 82%, transparent 100%);
}

.hero-inner {
  width: min(1180px, 100%);
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(380px, 0.9fr);
  gap: 56px;
  align-items: center;
}

.hero-copy {
  max-width: 720px;
}

.hero-kicker {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 22px;
  color: #0076ff;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0;
}

.hero-kicker span {
  width: 30px;
  height: 2px;
  background: #ffcf5a;
}

.hero-copy h1 {
  margin: 0;
  color: #071015;
  font-size: clamp(44px, 6vw, 84px);
  line-height: 0.98;
  font-weight: 900;
  letter-spacing: 0;
}

.hero-copy h1 span {
  display: block;
  color: #0076ff;
}

.hero-copy h1 b,
.hero-copy h1 em {
  display: block;
  color: #ff5f3d;
  font-style: normal;
  font-weight: inherit;
}

.hero-subtitle {
  max-width: 620px;
  margin: 28px 0 0;
  color: #475467;
  font-size: 19px;
  line-height: 1.85;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 36px;
}

.primary-button,
.ghost-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 52px;
  padding: 0 24px;
  border-radius: 6px;
  color: #ffffff;
  font-weight: 800;
  text-decoration: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.primary-button {
  background: linear-gradient(135deg, #0076ff 0%, #18c8ff 100%);
  color: #ffffff;
  box-shadow: 0 18px 44px rgba(0, 118, 255, 0.26);
}

.ghost-button {
  border: 1px solid rgba(0, 118, 255, 0.22);
  color: #0057c2;
  background: rgba(255, 255, 255, 0.74);
  box-shadow: 0 16px 38px rgba(16, 87, 154, 0.09);
}

.primary-button:hover,
.ghost-button:hover {
  transform: translateY(-2px);
}

.hero-proof {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  max-width: 620px;
  margin-top: 42px;
}

.hero-proof div {
  min-height: 84px;
  padding: 18px;
  border: 1px solid rgba(0, 118, 255, 0.12);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.82);
  box-shadow: 0 18px 42px rgba(16, 87, 154, 0.1);
}

.hero-proof strong,
.hero-proof span {
  display: block;
}

.hero-proof strong {
  color: #071015;
  font-size: 26px;
  line-height: 1;
}

.hero-proof span {
  margin-top: 8px;
  color: #667085;
  font-size: 13px;
}

.hero-dashboard {
  position: relative;
  min-height: 520px;
  padding: 22px;
  border: 1px solid rgba(0, 118, 255, 0.13);
  border-radius: 8px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.94), rgba(241, 248, 255, 0.76)),
  rgba(255, 255, 255, 0.76);
  box-shadow: 0 28px 90px rgba(16, 87, 154, 0.18);
  overflow: hidden;
  backdrop-filter: blur(18px);
}

.hero-dashboard::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(120deg, transparent 0%, rgba(0, 118, 255, 0.16) 42%, transparent 62%);
  transform: translateX(-110%);
  animation: dashboard-sheen 5s ease-in-out infinite;
}

.dashboard-header,
.dashboard-metrics,
.code-window {
  position: relative;
  z-index: 2;
}

.dashboard-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #667085;
  font-size: 12px;
  text-transform: uppercase;
}

.dashboard-header strong {
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 10px;
  border-radius: 6px;
  color: #064e3b;
  background: #b9ffd8;
}

.signal-map {
  position: relative;
  z-index: 1;
  height: 260px;
  margin: 28px 0 22px;
  border: 1px solid rgba(0, 118, 255, 0.12);
  border-radius: 8px;
  background: linear-gradient(rgba(0, 118, 255, 0.08) 1px, transparent 1px),
  linear-gradient(90deg, rgba(0, 118, 255, 0.08) 1px, transparent 1px),
  rgba(255, 255, 255, 0.58);
  background-size: 34px 34px;
  overflow: hidden;
}

.signal-line {
  position: absolute;
  left: 12%;
  width: 76%;
  height: 2px;
  background: linear-gradient(90deg, transparent, #4dd8ff, #ffcf5a, transparent);
  transform-origin: center;
  animation: signal-pulse 3.6s linear infinite;
}

.signal-line:nth-child(1) {
  top: 28%;
  transform: rotate(18deg);
}

.signal-line:nth-child(2) {
  top: 46%;
  transform: rotate(-9deg);
  animation-delay: 0.4s;
}

.signal-line:nth-child(3) {
  top: 62%;
  transform: rotate(7deg);
  animation-delay: 0.8s;
}

.signal-line:nth-child(4) {
  top: 75%;
  transform: rotate(-18deg);
  animation-delay: 1.2s;
}

.signal-line:nth-child(5) {
  top: 18%;
  transform: rotate(-24deg);
  animation-delay: 1.6s;
}

.signal-core {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 148px;
  height: 148px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(0, 118, 255, 0.32);
  border-radius: 8px;
  color: #0076ff;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 18px 54px rgba(0, 118, 255, 0.18);
  transform: translate(-50%, -50%) rotate(45deg);
}

.signal-inner {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transform: rotate(-45deg);
}

.signal-inner span {
  font-size: 42px;
  font-weight: 900;
  line-height: 1;
}

.signal-inner em {
  margin-top: 14px;
  color: #ff5f3d;
  font-size: 11px;
  font-style: normal;
  text-transform: uppercase;
}

.dashboard-metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.dashboard-metrics div {
  padding: 14px;
  border: 1px solid rgba(0, 118, 255, 0.11);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.78);
}

.dashboard-metrics span,
.dashboard-metrics strong {
  display: block;
}

.dashboard-metrics span {
  color: #667085;
  font-size: 12px;
}

.dashboard-metrics strong {
  margin-top: 8px;
  color: #071015;
  font-size: 20px;
}

.code-window {
  margin-top: 12px;
  padding: 16px;
  border-radius: 8px;
  background: #f7fbff;
  border: 1px solid rgba(0, 118, 255, 0.1);
}

.window-dots {
  display: flex;
  gap: 6px;
  margin-bottom: 14px;
}

.window-dots span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.window-dots span:nth-child(1) {
  background: #ff5f3d;
}

.window-dots span:nth-child(2) {
  background: #ffcf5a;
}

.window-dots span:nth-child(3) {
  background: #6dffb6;
}

.code-window pre {
  margin: 0;
  color: #0057c2;
  font-size: 13px;
  line-height: 1.7;
  white-space: pre-wrap;
}

.impact-strip {
  position: relative;
  z-index: 3;
  margin-top: -42px;
  padding: 0 24px;
}

.strip-inner {
  width: min(1180px, 100%);
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 26px 80px rgba(16, 87, 154, 0.12);
  overflow: hidden;
}

.strip-inner article {
  padding: 30px;
  border-right: 1px solid #eef1f6;
}

.strip-inner article:last-child {
  border-right: 0;
}

.strip-inner strong,
.strip-inner span {
  display: block;
}

.strip-inner strong {
  color: #071015;
  font-size: 38px;
  line-height: 1;
}

.strip-inner span {
  margin-top: 10px;
  color: #667085;
}

.service-section,
.process-section,
.about-section,
.advantage-section,
.contact-section {
  padding: 96px 24px;
}

.service-section {
  background: linear-gradient(180deg, #ffffff 0%, #f6faff 100%);
}

.section-heading {
  width: min(760px, 100%);
  margin: 0 auto 52px;
  text-align: center;
}

.section-heading.compact {
  margin-bottom: 38px;
}

.section-heading p,
.eyebrow {
  margin: 0 0 12px;
  color: #0076ff;
  font-size: 13px;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
}

.section-heading h2,
.about-content h2,
.contact-copy h2 {
  margin: 0;
  color: #071015;
  font-size: clamp(32px, 4vw, 54px);
  line-height: 1.1;
  font-weight: 900;
  letter-spacing: 0;
}

.section-heading span,
.contact-copy p,
.about-content p {
  display: block;
  margin-top: 18px;
  color: #667085;
  font-size: 17px;
  line-height: 1.8;
}

.service-grid {
  width: min(1180px, 100%);
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 24px;
}

.service-card {
  border: 1px solid #e8edf5;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.08);
  overflow: hidden;
  transition: transform 0.24s ease, box-shadow 0.24s ease, border-color 0.24s ease;
}

.service-card:hover {
  border-color: rgba(0, 118, 255, 0.36);
  box-shadow: 0 32px 90px rgba(15, 23, 42, 0.14);
  transform: translateY(-8px);
}

.service-image {
  position: relative;
  height: 232px;
  overflow: hidden;
}

.service-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.35s ease;
}

.service-card:hover .service-image img {
  transform: scale(1.06);
}

.service-image::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 26%, rgba(5, 7, 13, 0.78) 100%);
}

.service-image span {
  position: absolute;
  right: 18px;
  bottom: 16px;
  z-index: 1;
  color: #ffffff;
  font-size: 42px;
  font-weight: 900;
  line-height: 1;
}

.service-body {
  padding: 26px;
}

.service-icon {
  width: 46px;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;
  border-radius: 8px;
  color: #071015;
  background: #ffcf5a;
}

.service-body h3 {
  margin: 0 0 12px;
  color: #071015;
  font-size: 24px;
  font-weight: 900;
}

.service-body p {
  margin: 0;
  color: #667085;
  line-height: 1.8;
}

.tag-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 22px;
}

.tag-row span {
  padding: 7px 10px;
  border-radius: 6px;
  color: #0057c2;
  background: #edf6ff;
  font-size: 12px;
  font-weight: 700;
}

.process-section {
  background: linear-gradient(135deg, rgba(255, 207, 90, 0.16), transparent 36%),
  linear-gradient(180deg, #ffffff 0%, #f7fbff 100%);
}

.process-track {
  width: min(1180px, 100%);
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}

.process-card {
  min-height: 220px;
  padding: 26px;
  border: 1px solid #e8edf5;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 18px 52px rgba(15, 23, 42, 0.07);
}

.process-card span {
  display: inline-flex;
  margin-bottom: 32px;
  color: #ff5f3d;
  font-size: 38px;
  font-weight: 900;
  line-height: 1;
}

.process-card h3 {
  margin: 0 0 12px;
  color: #071015;
  font-size: 21px;
  font-weight: 900;
}

.process-card p {
  margin: 0;
  color: #667085;
  line-height: 1.75;
}

.about-section {
  width: min(1180px, 100%);
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
  gap: 56px;
  align-items: center;
}

.about-media {
  position: relative;
}

.about-media img {
  width: 100%;
  display: block;
  border-radius: 8px;
  box-shadow: 0 28px 80px rgba(15, 23, 42, 0.16);
}

.about-caption {
  position: absolute;
  right: 20px;
  bottom: 20px;
  min-width: 220px;
  padding: 18px;
  border-radius: 8px;
  color: #071015;
  background: rgba(255, 255, 255, 0.86);
  box-shadow: 0 18px 42px rgba(16, 87, 154, 0.18);
  backdrop-filter: blur(16px);
}

.about-caption strong,
.about-caption span {
  display: block;
}

.about-caption strong {
  font-size: 18px;
}

.about-caption span {
  margin-top: 6px;
  color: #667085;
}

.about-content p {
  margin-bottom: 0;
}

.value-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 28px;
}

.value-grid div {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 58px;
  padding: 14px;
  border: 1px solid #e8edf5;
  border-radius: 8px;
  background: #ffffff;
}

.value-grid i {
  color: #ff5f3d;
}

.value-grid span {
  color: #071015;
  font-weight: 800;
}

.advantage-section {
  background: radial-gradient(circle at 16% 18%, rgba(77, 216, 255, 0.2), transparent 28%),
  radial-gradient(circle at 88% 8%, rgba(255, 207, 90, 0.2), transparent 24%),
  linear-gradient(180deg, #f5fbff 0%, #ffffff 100%);
  color: #071015;
}

.section-heading.on-light h2 {
  color: #071015;
}

.section-heading.on-light span {
  color: #667085;
}

.advantage-grid {
  width: min(1180px, 100%);
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
}

.advantage-card {
  padding: 28px;
  border: 1px solid #e8edf5;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 22px 64px rgba(16, 87, 154, 0.09);
  transition: transform 0.24s ease, background 0.24s ease, border-color 0.24s ease;
}

.advantage-card:hover {
  border-color: rgba(0, 118, 255, 0.25);
  background: #ffffff;
  transform: translateY(-6px);
}

.advantage-card > i {
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  border-radius: 8px;
  color: #ffffff;
  background: linear-gradient(135deg, #0076ff, #18c8ff);
  font-size: 20px;
}

.advantage-card h3 {
  margin: 0 0 12px;
  color: #071015;
  font-size: 22px;
  font-weight: 900;
}

.advantage-card p {
  margin: 0;
  color: #667085;
  line-height: 1.75;
}

.advantage-card ul {
  display: grid;
  gap: 10px;
  margin: 22px 0 0;
  padding: 0;
  list-style: none;
}

.advantage-card li {
  display: flex;
  align-items: center;
  gap: 9px;
  color: #475467;
  font-size: 14px;
}

.advantage-card li i {
  color: #ff5f3d;
}

.contact-section {
  background: #ffffff;
}

.contact-section {
  width: min(1180px, 100%);
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(360px, 0.76fr);
  gap: 56px;
  align-items: start;
}

.contact-list {
  display: grid;
  gap: 14px;
  margin-top: 30px;
}

.contact-list div {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 18px;
  border: 1px solid #e8edf5;
  border-radius: 8px;
  background: #ffffff;
}

.contact-list i {
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: #ffffff;
  background: #0076ff;
}

.contact-list span,
.contact-list strong {
  display: block;
}

.contact-list strong {
  margin-bottom: 5px;
  color: #071015;
}

.contact-list span {
  color: #667085;
}

.contact-form {
  display: grid;
  gap: 18px;
  padding: 28px;
  border: 1px solid #e8edf5;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 26px 80px rgba(15, 23, 42, 0.12);
}

.contact-form label {
  display: grid;
  gap: 9px;
  color: #071015;
  font-size: 14px;
  font-weight: 800;
}

.contact-form :deep(.el-input__wrapper),
.contact-form :deep(.el-textarea__inner) {
  border-radius: 6px;
  box-shadow: 0 0 0 1px #d9e2ef inset;
}

.contact-form :deep(.el-input__wrapper.is-focus),
.contact-form :deep(.el-textarea__inner:focus) {
  box-shadow: 0 0 0 1px #0076ff inset;
}

.contact-form button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 52px;
  border: 0;
  border-radius: 6px;
  color: #10131f;
  background: linear-gradient(135deg, #ffcf5a 0%, #6dffb6 100%);
  font-weight: 900;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.contact-form button:hover {
  box-shadow: 0 18px 44px rgba(109, 255, 182, 0.28);
  transform: translateY(-2px);
}

.site-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 40px max(24px, calc((100% - 1180px) / 2));
  color: #667085;
  background: #ffffff;
  border-top: 1px solid #e8edf5;
}

.site-footer img {
  width: 150px;
  padding: 8px 10px;
  border-radius: 6px;
  background: #ffffff;
  box-shadow: 0 10px 28px rgba(16, 87, 154, 0.08);
}

.site-footer p {
  margin: 12px 0 0;
}

@keyframes dashboard-sheen {
  0%,
  46% {
    transform: translateX(-110%);
  }
  76%,
  100% {
    transform: translateX(110%);
  }
}

@keyframes signal-pulse {
  0% {
    opacity: 0.15;
    filter: hue-rotate(0deg);
  }
  45% {
    opacity: 0.95;
  }
  100% {
    opacity: 0.15;
    filter: hue-rotate(70deg);
  }
}

@media (max-width: 1024px) {
  .hero-inner,
  .about-section,
  .contact-section {
    grid-template-columns: 1fr;
  }

  .hero-dashboard {
    min-height: 460px;
  }

  .service-grid,
  .advantage-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .process-track {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 860px) {
  .site-nav {
    top: 10px;
    width: calc(100% - 20px);
  }

  .nav-links {
    display: none;
  }

  .brand {
    width: 154px;
  }

  .brand img {
    width: 132px;
  }

  .hero-section {
    min-height: auto;
    padding-top: 118px;
  }

  .hero-proof,
  .strip-inner,
  .service-grid,
  .process-track,
  .advantage-grid,
  .contact-section {
    grid-template-columns: 1fr;
  }

  .strip-inner article {
    border-right: 0;
    border-bottom: 1px solid #eef1f6;
  }

  .strip-inner article:last-child {
    border-bottom: 0;
  }

  .hero-dashboard {
    min-height: 420px;
  }

  .signal-map {
    height: 220px;
  }

  .site-footer {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (max-width: 560px) {
  .site-nav {
    height: 62px;
    padding: 0 10px;
  }

  .nav-cta {
    height: 38px;
    padding: 0 12px;
  }

  .hero-section,
  .service-section,
  .process-section,
  .about-section,
  .advantage-section,
  .contact-section {
    padding-left: 16px;
    padding-right: 16px;
  }

  .hero-copy h1 {
    font-size: clamp(40px, 12vw, 52px);
  }

  .hero-subtitle,
  .section-heading span,
  .contact-copy p,
  .about-content p {
    font-size: 16px;
  }

  .hero-actions a {
    width: 100%;
  }

  .dashboard-metrics {
    grid-template-columns: 1fr;
  }

  .value-grid {
    grid-template-columns: 1fr;
  }

  .about-caption {
    position: static;
    margin-top: 12px;
  }
}
</style>
